import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { getCodexConceptPage, type CodexConceptPage } from "@/lib/codex.functions";
import { getGate } from "@/lib/gates";
import { ConceptWorks } from "@/components/site/ConceptWorks";
import { absoluteUrl } from "@/lib/site";
import { breadcrumbs, ldScript, orgRef, pageNode, personRef, seoMeta } from "@/lib/schema";

export const Route = createFileRoute("/codex/$slug")({
  loader: async ({ params }) => {
    const page = await getCodexConceptPage({ data: { slug: params.slug } });
    if (!page) throw notFound();
    return page;
  },
  head: ({ loaderData }) => {
    if (!loaderData) {
      return { meta: [{ title: "Concept not found — The KAfrequency Codex" }, { name: "robots", content: "noindex" }] };
    }
    const { concept, related } = loaderData as CodexConceptPage;
    const path = `/codex/${concept.slug}`;
    const url = absoluteUrl(path);
    const description = (concept.essence?.trim() || concept.definition).slice(0, 300);

    const term = {
      "@type": "DefinedTerm",
      "@id": `${url}#concept`,
      name: concept.title,
      description: concept.definition,
      url,
      ...(concept.cluster ? { termCode: concept.cluster } : {}),
      inDefinedTermSet: {
        "@type": "DefinedTermSet",
        "@id": `${absoluteUrl("/codex")}#codex`,
        name: "The KAfrequency Codex",
        url: absoluteUrl("/codex"),
      },
      ...(related.length
        ? {
            isRelatedTo: related.map((r) => ({
              "@type": "DefinedTerm",
              "@id": `${absoluteUrl(`/codex/${r.slug}`)}#concept`,
              name: r.title,
              url: absoluteUrl(`/codex/${r.slug}`),
            })),
          }
        : {}),
    };

    return {
      ...seoMeta({
        title: `${concept.title} · The KAfrequency Codex`,
        description,
        path,
        ogType: "article",
      }),
      scripts: [
        ldScript(
          pageNode({
            type: "WebPage",
            id: `${url}#page`,
            url,
            name: `${concept.title} · The KAfrequency Codex`,
            description,
            mainEntity: term,
            about: [personRef(), orgRef()],
            extra: { author: personRef(), publisher: orgRef() },
          }),
        ),
        ldScript(breadcrumbs([["Codex", "/codex"], [concept.title, path]])),
      ],
    };
  },
  component: ConceptDetail,
  notFoundComponent: () => (
    <div className="mx-auto max-w-2xl px-4 py-24 text-center">
      <h1 className="font-serif text-3xl">That concept is not yet in the Codex.</h1>
      <Link to="/codex" className="mt-6 inline-block text-gold hover:text-gold-luminous">
        ← Return to the Codex
      </Link>
    </div>
  ),
  errorComponent: ({ error }) => (
    <div className="mx-auto max-w-2xl px-4 py-24 text-center">
      <p className="text-sm text-muted-foreground">{error.message}</p>
      <Link to="/codex" className="mt-6 inline-block text-gold hover:text-gold-luminous">
        ← Return to the Codex
      </Link>
    </div>
  ),
});

function ConceptDetail() {
  const { concept, related, links } = Route.useLoaderData() as CodexConceptPage;

  const gateLinks = links.filter((l) => l.entity_kind === "gate");

  return (
    <div className="mx-auto max-w-4xl px-4 py-16 sm:px-6 sm:py-24">
      <nav aria-label="Breadcrumb" className="mb-8 text-xs uppercase tracking-[0.3em] text-muted-foreground">
        <Link to="/codex" className="hover:text-gold">The Codex</Link>
        <span className="mx-2 text-gold/50">·</span>
        <span className="text-gold">{concept.title}</span>
      </nav>

      <header className="relative overflow-hidden rounded-3xl border hairline bg-obsidian/60 p-8 backdrop-blur sm:p-12">
        <div aria-hidden className="absolute -right-24 -top-24 h-64 w-64 rounded-full bg-gold/10 blur-3xl" />
        <p className="text-[10px] uppercase tracking-[0.4em] text-gold">
          Concept{concept.cluster ? ` · ${concept.cluster}` : ""}
        </p>
        <h1 className="mt-4 font-serif text-4xl leading-tight text-gold-luminous sm:text-6xl">
          {concept.title}
        </h1>
        {concept.essence && (
          <p className="mt-6 font-serif text-xl italic leading-relaxed text-ivory/90">
            &ldquo;{concept.essence}&rdquo;
          </p>
        )}
        <p className="mt-6 text-base text-muted-foreground sm:text-lg">{concept.definition}</p>
      </header>

      {gateLinks.length > 0 && (
        <section className="mt-12">
          <h2 className="font-serif text-2xl text-gold-luminous">Gates that carry this concept</h2>
          <ul className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {gateLinks.map((l) => {
              const n = Number(l.entity_ref);
              const g = getGate(n);
              if (!g) return null;
              return (
                <li key={l.entity_ref}>
                  <Link
                    to="/gates/$n"
                    params={{ n: String(n) }}
                    className="group flex h-full flex-col gap-2 rounded-xl border hairline bg-obsidian/60 p-5 backdrop-blur transition-all hover:border-gold hover:bg-obsidian/80"
                  >
                    <span className="text-[10px] uppercase tracking-[0.3em] text-gold">
                      Gate {String(n).padStart(2, "0")}
                    </span>
                    <span className="font-serif text-lg text-ivory group-hover:text-gold-luminous">
                      {g.title}
                    </span>
                    <span className="line-clamp-2 text-xs text-muted-foreground">{g.reflection}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      <ConceptWorks links={links} conceptSlug={concept.slug} />

      {related.length > 0 && (
        <section className="mt-12">
          <h2 className="font-serif text-2xl text-gold-luminous">Related concepts</h2>
          <ul className="mt-4 grid gap-3 sm:grid-cols-2">
            {related.map((r) => (
              <li key={r.slug}>
                <Link
                  to="/codex/$slug"
                  params={{ slug: r.slug }}
                  className="group flex h-full flex-col gap-1.5 rounded-xl border hairline bg-obsidian/60 p-5 backdrop-blur transition-all hover:border-gold hover:bg-obsidian/80"
                >
                  <span className="font-serif text-lg text-ivory group-hover:text-gold-luminous">{r.title}</span>
                  {r.essence && (
                    <span className="line-clamp-2 text-xs text-muted-foreground">{r.essence}</span>
                  )}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="mt-16 flex flex-wrap items-center justify-between gap-3 rounded-md border hairline p-4 text-sm">
        <span className="text-foreground/70">Concepts weave through every part of the ecosystem.</span>
        <Link to="/codex" className="text-gold underline decoration-gold/40 underline-offset-4 hover:text-gold-luminous hover:decoration-gold">
          Explore the Codex →
        </Link>
      </div>
    </div>
  );
}
